import { X, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CartItem } from "@/context/CartContext";

interface OrderSummaryCardProps {
  items: CartItem[];
  onUpdateQuantity?: (id: string, quantity: number) => void;
  onRemoveItem?: (id: string) => void;
}

export const OrderSummaryCard = ({ 
  items, 
  onUpdateQuantity, 
  onRemoveItem 
}: OrderSummaryCardProps) => {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal >= 50 ? 0 : 6.95;
  const total = subtotal + shipping;

  return (
    <div className="bg-card rounded-2xl border-2 border-gold/20 p-6 space-y-6 shadow-lg">
      <h2 className="font-serif text-2xl font-bold text-gold">Order Samenvatting</h2>

      {/* Cart Items */}
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="flex gap-4 items-start pb-4 border-b border-muted">
            {item.image && (
              <img
                src={item.image}
                alt={item.name}
                className="w-20 h-20 object-cover rounded-lg"
              />
            )}
            
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">{item.name}</h3>
              <p className="text-sm text-muted-foreground">{item.size}</p>
              <p className="text-gold font-bold mt-1">€{item.price.toFixed(2)}</p>

              {/* Quantity Controls */}
              {onUpdateQuantity && (
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7"
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-8 text-center text-sm font-semibold">
                    {item.quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7"
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>

            {onRemoveItem && (
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8"
                onClick={() => onRemoveItem(item.id)}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Discount Code */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Kortingscode</label>
        <div className="flex gap-2">
          <Input
            placeholder="Voer code in"
            className="flex-1"
          />
          <Button variant="outline" className="shrink-0">
            Toepassen
          </Button>
        </div>
      </div>

      {/* Pricing Summary */}
      <div className="space-y-3 pt-4 border-t border-muted">
        <div className="flex justify-between text-foreground">
          <span>Subtotaal</span>
          <span className="font-semibold">€{subtotal.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between text-foreground">
          <span>Verzendkosten</span>
          <span className="font-semibold">
            {shipping === 0 ? (
              <span className="text-emerald-600">Gratis</span>
            ) : (
              `€${shipping.toFixed(2)}`
            )}
          </span>
        </div>

        {subtotal < 50 && (
          <p className="text-xs text-muted-foreground">
            Nog €{(50 - subtotal).toFixed(2)} tot gratis verzending
          </p>
        )}

        <div className="flex justify-between text-lg font-bold text-gold pt-3 border-t border-muted">
          <span>Totaal</span>
          <span>€{total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};
